import { CONTROL_NAME } from '../../utils/blocks/types'
import type { AdditionalVariableHandler, BlockVariablesHander } from '../types'

import {
  getValuesFromAddressBlock,
  getVariablesFromAddressBlock
} from './AddressBlockHandler'
import {
  getValuesFromAvailabilityBlock,
  getVariablesFromAvailabilityBlock
} from './AvailabilityBlockHandler'
import {
  getValuesFromBooleanInputBlock,
  getVariablesFromBooleanInputBlock
} from './BooleanInputHandler'
import {
  getValuesFromConsentsBlock,
  getVariablesFromConsentsBlock
} from './ConsentsBlockHandler'
import {
  getValuesFromDateBlock,
  getVaribalesFromDateBlock
} from './DateBlockHandler'
import {
  getVariablesFromDummyBlock,
  getValuesFromDummyBlock
} from './DummyBlockHandler'
import {
  getValuesFromEntityAttributeBlock,
  getVariablesFromEntityAttributeBlock
} from './EntityAttributeBlockHandler'
import {
  getVariablesForJourneyContext,
  getValuesForJourneyContext
} from './JourneyContextHandler'
import {
  getValuesForMetadata,
  getVariablesForMetadata
} from './MetadataHandler'
import {
  getValuesFromPaymentMethodBlock,
  getVariablesFromPaymentMethodBlock
} from './PaymentMethodBlockHandler'
import {
  getValuesFromPersonalInformationBlock,
  getVariablesFromPersonalInformationBlock
} from './PersonalInformationBlockHandler'
import {
  getValuesFromProductSelectionBlock,
  getVariablesFromProductSelectionBlock
} from './ProductSelectionBlockHandler'
import {
  getVariabelsFromPvRoofPlannerBlock,
  getValuesFromPvRoofPlannerBlock
} from './PvRoofPlannerBlockHandler'
import {
  getValuesFromShoppingCart,
  getVariablesFromShoppingCart
} from './ShoppingCartHandler'
import {
  getNumberValuesFromSimpleInputBlock,
  getTextValuesFromSimpleInputBlock,
  getVariablesFromSimpleInputBlock
} from './SimpleInputHandler'

/**
 * Some block handlers are shared between blocks types
 */
const sharedBlockVariablesHandlers: Record<
  | 'PersonalInformationBlockHandler'
  | 'SimpleInputBlockHandler'
  | 'DummyBlockHandler',
  BlockVariablesHander
> = {
  PersonalInformationBlockHandler: {
    getVariablesFromBlock: getVariablesFromPersonalInformationBlock,
    getValuesForExport: getValuesFromPersonalInformationBlock
  },
  SimpleInputBlockHandler: {
    getVariablesFromBlock: getVariablesFromSimpleInputBlock,
    getValuesForExport: getTextValuesFromSimpleInputBlock
  },
  DummyBlockHandler: {
    getVariablesFromBlock: getVariablesFromDummyBlock,
    getValuesForExport: getValuesFromDummyBlock
  }
}

export const blockVariablesHandlerByBlock: {
  [key in CONTROL_NAME]?: BlockVariablesHander
} = {
  [CONTROL_NAME.ADDRESS_BLOCK]: {
    getVariablesFromBlock: getVariablesFromAddressBlock,
    getValuesForExport: getValuesFromAddressBlock
  },
  [CONTROL_NAME.PRODUCT_SELECTION_CONTROL]: {
    getVariablesFromBlock: getVariablesFromProductSelectionBlock,
    getValuesForExport: getValuesFromProductSelectionBlock
  },
  [CONTROL_NAME.PERSONAL_INFORMATION_CONTROL]:
    sharedBlockVariablesHandlers.PersonalInformationBlockHandler,
  [CONTROL_NAME.SUMMARY_CONTROL]:
    sharedBlockVariablesHandlers.DummyBlockHandler,
  [CONTROL_NAME.PRODUCT_CATEGORY_CONTROL]:
    sharedBlockVariablesHandlers.SimpleInputBlockHandler,
  [CONTROL_NAME.PAYMENT_CONTROL]: {
    getVariablesFromBlock: getVariablesFromPaymentMethodBlock,
    getValuesForExport: getValuesFromPaymentMethodBlock
  },
  [CONTROL_NAME.DATE_PICKER]: {
    getVariablesFromBlock: getVaribalesFromDateBlock,
    getValuesForExport: getValuesFromDateBlock
  },
  [CONTROL_NAME.NUMBER_INPUT_CONTROL]: {
    getVariablesFromBlock: getVariablesFromSimpleInputBlock,
    getValuesForExport: getNumberValuesFromSimpleInputBlock
  },
  [CONTROL_NAME.CONSENTS_CONTROL]: {
    getVariablesFromBlock: getVariablesFromConsentsBlock,
    getValuesForExport: getValuesFromConsentsBlock
  },
  [CONTROL_NAME.AVAILABILITY_CHECK]: {
    getVariablesFromBlock: getVariablesFromAvailabilityBlock,
    getValuesForExport: getValuesFromAvailabilityBlock
  },
  [CONTROL_NAME.MULTICHOICE_CONTROL]:
    sharedBlockVariablesHandlers.SimpleInputBlockHandler,
  [CONTROL_NAME.BOOLEAN_CONTROL]: {
    getVariablesFromBlock: getVariablesFromBooleanInputBlock,
    getValuesForExport: getValuesFromBooleanInputBlock
  },
  [CONTROL_NAME.ENUM_CONTROL]:
    sharedBlockVariablesHandlers.SimpleInputBlockHandler,
  [CONTROL_NAME.TEXTFIELD_CONTROL]:
    sharedBlockVariablesHandlers.SimpleInputBlockHandler,
  [CONTROL_NAME.ENTITY_ATTRIBUTE_CONTROL]: {
    getVariablesFromBlock: getVariablesFromEntityAttributeBlock,
    getValuesForExport: getValuesFromEntityAttributeBlock
  },
  [CONTROL_NAME.PV_ROOF_PLANNER_CONTROL]: {
    getVariablesFromBlock: getVariabelsFromPvRoofPlannerBlock,
    getValuesForExport: getValuesFromPvRoofPlannerBlock
  },
  [CONTROL_NAME.CONTACT_CONTROL]:
    sharedBlockVariablesHandlers.PersonalInformationBlockHandler,
  [CONTROL_NAME.ACCOUNT_CONTROL]:
    sharedBlockVariablesHandlers.PersonalInformationBlockHandler
}

export const additionalHandlers: Record<
  'metadata' | 'journey_context' | 'shopping_cart',
  AdditionalVariableHandler
> = {
  ['metadata']: {
    nameLabel: 'pdf_summary.additional.metadata',
    icon: 'note_stack',
    includePrefix: true,
    getVariables: getVariablesForMetadata,
    getValues: getValuesForMetadata
  },
  ['journey_context']: {
    nameLabel: 'pdf_summary.additional.journey_context',
    icon: 'note_stack',
    includePrefix: true,
    getVariables: getVariablesForJourneyContext,
    getValues: getValuesForJourneyContext
  },
  ['shopping_cart']: {
    nameLabel: 'pdf_summary.additional.shopping_cart',
    icon: 'note_stack',
    includePrefix: false,
    getVariables: getVariablesFromShoppingCart,
    getValues: getValuesFromShoppingCart
  }
}
